import { useEffect, useRef } from 'react';
import OneSignal from 'react-onesignal';
import { api } from '@/lib/api';
import { getOrCreateDeviceId } from '@/lib/deviceId';

interface UseOneSignalOptions {
  userId: string | undefined;
  isAuthenticated: boolean;
}

/**
 * Hook to initialize OneSignal Web Push SDK
 * Fetches push config from backend (per-tenant), initializes SDK,
 * and registers device token.
 */
export function useOneSignal({ userId, isAuthenticated }: UseOneSignalOptions): void {
  const initializedRef = useRef(false);
  const tokenRef = useRef<string | null>(null);

  useEffect(() => {
    if (!isAuthenticated || !userId || initializedRef.current) {
      return;
    }

    let cancelled = false;

    const init = async () => {
      try {
        // 1. Fetch push config from backend (dynamic per tenant)
        const response = await api.get('users/me/push-config');
        const pushConfig = response.data?.data;

        if (!pushConfig || !pushConfig.appId) {
          // No push provider configured for this tenant - exit silently
          return;
        }

        if (cancelled) return;

        // 2. Initialize OneSignal with tenant's App ID
        // SW path/scope dedicados: o SW da app (Workbox) controla `/`, o SW do
        // OneSignal controla apenas `/push/onesignal/`. Sem isso, o OneSignal
        // registrava OneSignalSDKWorker.js em `/`, virando "waiting" do mesmo
        // registration do sw.js — o que fazia o toast "Nova versão disponível"
        // aparecer eternamente porque o OneSignal SW não responde a SKIP_WAITING.
        await OneSignal.init({
          appId: pushConfig.appId,
          allowLocalhostAsSecureOrigin: import.meta.env.DEV,
          notificationClickHandlerMatch: 'origin',
          notificationClickHandlerAction: 'focus',
          serviceWorkerPath: 'push/onesignal/OneSignalSDKWorker.js',
          serviceWorkerParam: { scope: '/push/onesignal/' },
        });

        if (cancelled) return;

        // 3. Set external user ID for targeting
        await OneSignal.login(userId);

        // 4. Request notification permission
        await OneSignal.Notifications.requestPermission();

        if (cancelled) return;

        // 5. Get subscription ID and update device token in backend
        // device identity (deviceId/platform/browser) was already registered at login time (Phase B)
        const subscriptionId = OneSignal.User.PushSubscription.id;
        if (subscriptionId) {
          tokenRef.current = subscriptionId;

          await api.post('users/me/devices', {
            deviceId: getOrCreateDeviceId(),
            token: subscriptionId,
          });
        }

        initializedRef.current = true;
      } catch (error) {
        console.warn('[OneSignal] Initialization failed:', error);
      }
    };

    init();

    return () => {
      cancelled = true;
    };
  }, [userId, isAuthenticated]);
}

/**
 * Logout from OneSignal and unregister device
 */
export async function oneSignalLogout(): Promise<void> {
  try {
    await OneSignal.logout();
  } catch {
    // Ignore errors during logout
  }
}

